<!--
  IdentityMenu — the dropdown that hangs off the avatar pill in AppBar.
  Reused across SiteShell / RemoteShell / AdminShell so the user can always
  see who they are, jump between the three sections, and sign out — from
  any page in the SPA. Owns its own open/close state; closes on outside
  click, Escape, and route change.
-->
<script lang="ts">
  import { onMount } from "svelte";

  import { isAdmin } from "../admin";
  import {
    AUTH_EVENT,
    clearLocalTokens,
    endSessionWithIdp,
    getIdentity,
    type Identity,
  } from "../auth";
  import { resolveAppConfig } from "../config";
  import { go, router, type Section } from "../router.svelte";

  let open = $state(false);
  let identity = $state<Identity | null>(getIdentity());
  let canAdmin = $state(false);
  let triggerEl: HTMLButtonElement | undefined = $state();
  let menuEl: HTMLDivElement | undefined = $state();

  function refresh() {
    identity = getIdentity();
    if (identity) {
      void isAdmin().then((v) => (canAdmin = v));
    } else {
      canAdmin = false;
    }
  }

  // Single initial character for the avatar disc. Falls back to "?" when
  // the JWT is missing a usable display name (shouldn't happen on KC, but
  // ADFS/Kestrel edge cases exist).
  const initial = $derived(
    (identity?.name || identity?.email || "?").trim().charAt(0).toUpperCase() || "?",
  );

  // Close the menu on every navigation so the user doesn't see a stale
  // anchor after they jump sections.
  $effect(() => {
    void router.path;
    open = false;
  });

  function onDocClick(e: MouseEvent) {
    if (!open) return;
    const t = e.target as Node | null;
    if (triggerEl?.contains(t) || menuEl?.contains(t)) return;
    open = false;
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") open = false;
  }

  onMount(() => {
    refresh();
    document.addEventListener("click", onDocClick, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener(AUTH_EVENT, refresh);
    return () => {
      document.removeEventListener("click", onDocClick, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener(AUTH_EVENT, refresh);
    };
  });

  function jump(e: MouseEvent, to: string) {
    open = false;
    go(e, to);
  }

  async function signOut() {
    open = false;
    // Dev-only convenience: when the OIDC URLs aren't real (e.g. password-
    // grant dev), there's no IdP session to destroy — wipe local tokens
    // and bounce home. In prod we go through the IdP's end-session endpoint
    // so the SSO cookie is invalidated; otherwise the next /authorize round
    // trip would silently log the user back in.
    if (import.meta.env.DEV) {
      clearLocalTokens();
      window.dispatchEvent(new Event(AUTH_EVENT));
      router.navigate("/");
      return;
    }
    try {
      const cfg = await resolveAppConfig();
      endSessionWithIdp(cfg.oidc.endSessionUrl || "", `${window.location.origin}/`);
    } catch {
      clearLocalTokens();
      window.dispatchEvent(new Event(AUTH_EVENT));
      router.navigate("/");
    }
  }

  // Section rows. The active section is determined by the router. The
  // Admin row is hidden for non-admins so we don't paint a dead link.
  type Row = { section: Section; label: string; href: string; show: boolean };
  const sectionRows = $derived<Row[]>([
    { section: "site", label: "Home", href: "/", show: true },
    { section: "remote", label: "Remote Control", href: "/remote", show: true },
    { section: "admin", label: "Admin Console", href: "/admin", show: canAdmin },
  ]);
</script>

{#if identity}
  <div class="relative">
    <button
      bind:this={triggerEl}
      type="button"
      onclick={() => (open = !open)}
      aria-label="Account menu"
      aria-haspopup="menu"
      aria-expanded={open}
      class="identity-trigger group flex items-center gap-2 rounded-full border border-line-strong/60 bg-white/[0.04] py-1 pl-1 pr-2 text-sm transition hover:border-line-strong hover:bg-white/[0.08]"
    >
      <span class="avatar grid h-7 w-7 place-items-center rounded-full text-[12px] font-semibold text-white">
        {initial}
      </span>
      <span class="hidden max-w-[8rem] truncate text-ink-dim sm:inline">
        {identity.name}
      </span>
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        class="text-ink-faint transition group-aria-expanded:rotate-180"
        aria-hidden="true"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </button>

    {#if open}
      <div
        bind:this={menuEl}
        role="menu"
        class="menu absolute right-0 z-50 mt-2 w-64 origin-top-right overflow-hidden rounded-xl border border-line-strong/80 bg-surface/95 shadow-2xl backdrop-blur"
      >
        <div class="px-4 py-3">
          <div class="truncate text-sm font-medium text-ink">{identity.name}</div>
          {#if identity.email && identity.email !== identity.name}
            <div class="truncate text-xs text-ink-faint">{identity.email}</div>
          {/if}
        </div>
        <div class="border-t border-line"></div>
        <nav class="py-1">
          {#each sectionRows as row (row.section)}
            {#if row.show}
              {@const active = router.section === row.section}
              <a
                href={row.href}
                role="menuitem"
                aria-current={active ? "page" : undefined}
                onclick={(e) => jump(e, row.href)}
                class={`flex items-center gap-2.5 px-4 py-2 text-sm transition ${
                  active
                    ? "bg-accent-soft/60 text-ink"
                    : "text-ink-dim hover:bg-white/5 hover:text-ink"
                }`}
              >
                <span
                  class={`h-2 w-2 rounded-full ${active ? "bg-accent-hi" : "bg-ink-faint/40"}`}
                  aria-hidden="true"
                ></span>
                {row.label}
              </a>
            {/if}
          {/each}
        </nav>
        <div class="border-t border-line"></div>
        <button
          type="button"
          role="menuitem"
          onclick={signOut}
          class="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-ink-dim transition hover:bg-white/5 hover:text-ink"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
          </svg>
          Sign out
        </button>
      </div>
    {/if}
  </div>
{/if}

<style>
  /* The avatar disc — solid indigo→hi gradient. Uses the SPA tokens so a
     light-mode theme swap would be a one-liner. */
  .avatar {
    background: linear-gradient(135deg, var(--color-accent), var(--color-accent-hi));
  }

  /* Menu enter — a 120ms slide + fade that respects reduced-motion. */
  .menu {
    animation: menu-in 120ms cubic-bezier(0.2, 0.7, 0.2, 1) both;
  }
  @keyframes menu-in {
    from {
      opacity: 0;
      transform: translateY(-4px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .menu {
      animation: none;
    }
  }
</style>
